/* eslint-disable react/no-unescaped-entities */
import Layout from "../components/Layout";

export default function CommunityGuidelines() {
  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-8" data-testid="community-guidelines-page">
        <header>
          <div className="label-mystic text-secondary">Community · Last updated February 12, 2026</div>
          <h1 className="font-serif text-5xl mt-3">Community Guidelines</h1>
          <p className="text-foreground/65 mt-4 leading-relaxed">
            Cauldron is a sacred space where seekers and practitioners meet with intention. These Community Guidelines apply to every post, clip, comment, livestream, message, review, and profile across the web app, iOS app, and Android app. By using Cauldron you agree to follow them — violations may result in content removal, account suspension, or permanent ban.
          </p>
        </header>

        <Section title="Our Principles">
          <ol className="list-decimal pl-5 space-y-1.5">
            <li><strong>Respect the seeker, respect the practitioner.</strong> Treat every member as you would want to be treated.</li>
            <li><strong>Be authentic.</strong> Don't pretend to be someone you're not. Don't promise outcomes you can't deliver.</li>
            <li><strong>Protect the vulnerable.</strong> Many seekers come here in pain. Don't exploit them.</li>
            <li><strong>Keep it legal.</strong> If it would land you in court offline, it doesn't belong here.</li>
          </ol>
        </Section>

        <Section title="Zero-Tolerance: Immediate Permanent Ban">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Child sexual abuse material, sexualized depictions of minors, or grooming behavior</li>
            <li>Credible threats of violence against any person or group</li>
            <li>Doxxing, swatting, or publishing private identifying information</li>
            <li>Sexual content involving non-consent, blackmail, or coercion</li>
            <li>Terrorist content, glorification of mass violence, or recruitment for violent extremism</li>
            <li>Non-consensual intimate imagery ("revenge porn")</li>
            <li>Selling, soliciting, or trafficking humans, illegal drugs, weapons, or stolen goods</li>
          </ul>
          <p className="mt-3">We report this content to NCMEC and law enforcement as required by law.</p>
        </Section>

        <Section title="Prohibited Content &amp; Behavior">
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>Harassment &amp; hate</strong> — slurs, targeted abuse, dehumanizing speech based on race, ethnicity, religion, gender, gender identity, sexual orientation, disability, immigration status, or age</li>
            <li><strong>Self-harm &amp; eating disorders</strong> — content that encourages, glorifies, or provides instructions for self-harm, suicide, or eating disorders. (Recovery stories shared with care are welcome.)</li>
            <li><strong>Medical / mental-health claims</strong> — practitioners may not diagnose, prescribe, or guarantee cures. Always recommend professional care for serious conditions.</li>
            <li><strong>Financial advice &amp; "guaranteed wins"</strong> — no investment guarantees, no "manifest your way to $100K this week" scams, no lottery prediction sales</li>
            <li><strong>Impersonation</strong> — claiming to be another practitioner, a celebrity, or a deceased person without clear disclosure</li>
            <li><strong>Fraud &amp; deception</strong> — fake reviews, fake credentials, fabricated lineage, fake before/after photos, fake testimonials</li>
            <li><strong>Spam, scams, and pyramid schemes</strong> — including off-platform sales designed to evade fees</li>
            <li><strong>Sexually explicit content</strong> — Cauldron is not an adult content platform. Nudity in artistic, ritual, or anatomical-education contexts may be allowed with a content warning; explicit sexual acts are not</li>
            <li><strong>IP infringement</strong> — don't post copyrighted material, branded logos, or another person's likeness without rights. See our <a href="/terms" className="text-secondary underline">DMCA policy</a></li>
            <li><strong>Malware, phishing, and scraping</strong> — no malicious links, no automated scraping of profiles or content</li>
            <li><strong>Underage users</strong> — Cauldron is 18+. Minors must not create accounts</li>
          </ul>
        </Section>

        <Section title="Practitioner-Specific Standards">
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>No outcome guarantees.</strong> You may not promise specific results ("I will make your ex come back," "100% accurate readings").</li>
            <li><strong>Honest lineage.</strong> Claims of initiation, ordination, or tradition must be truthful. Fraudulent lineage results in permanent ban.</li>
            <li><strong>Pricing transparency.</strong> Disclose all fees upfront. No bait-and-switch.</li>
            <li><strong>Boundaries.</strong> Maintain professional boundaries with clients. No romantic / sexual solicitation of clients during or after sessions.</li>
            <li><strong>Vulnerable client protections.</strong> If a client is in crisis, recommend appropriate professional support (hotlines, therapy). Do not exploit grief, illness, or financial hardship.</li>
            <li><strong>Privacy.</strong> Do not share a client's name, story, or photo without explicit written consent.</li>
          </ul>
        </Section>

        <Section title="Reporting &amp; Blocking">
          <p className="mb-2"><strong>To report content or a user:</strong></p>
          <ul className="list-disc pl-5 space-y-1.5 mb-3">
            <li>Tap the "⋯" menu on any post, comment, clip, livestream, or profile → "Report"</li>
            <li>Pick a category (harassment, scam, self-harm, IP, other) and add a short note</li>
            <li>Or email <a href="mailto:trust@thecauldron.app" className="text-secondary underline">trust@thecauldron.app</a> with screenshots and links</li>
          </ul>
          <p className="mb-2"><strong>To block a user:</strong> open their profile → "⋯" → "Block." Blocked users cannot message you, see your posts, or book your services.</p>
          <p className="mb-2"><strong>To mute notifications without blocking:</strong> open the chat → "⋯" → "Mute."</p>
          <p><strong>Filtering objectionable content:</strong> In Profile → Settings → Content Filters, you can hide content tagged with mature themes (death work, hex work, etc.).</p>
        </Section>

        <Section title="Our Response Times">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Reports of <strong>imminent harm</strong> (self-harm, threats, CSAM): within 1 hour</li>
            <li>Reports of <strong>harassment, hate, fraud</strong>: within 24 hours</li>
            <li>Reports of <strong>IP infringement, impersonation</strong>: within 72 hours (DMCA: 5 business days)</li>
            <li>Reports of <strong>general policy violations</strong>: within 7 days</li>
          </ul>
          <p className="mt-3">We may extend timelines during weekends or holidays. We notify reporters when action is taken.</p>
        </Section>

        <Section title="Enforcement Actions">
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>Warning</strong> — for first-time minor violations</li>
            <li><strong>Content removal</strong> — the offending post / comment / clip is taken down</li>
            <li><strong>Feature restriction</strong> — temporary loss of posting, livestreaming, or payout privileges</li>
            <li><strong>Suspension</strong> — 7-day, 30-day, or 90-day timeout</li>
            <li><strong>Permanent ban</strong> — account terminated, payouts withheld pending dispute review, IP and device blocklisted</li>
          </ul>
          <p className="mt-3">All enforcement decisions are logged. You may appeal by emailing <a href="mailto:appeals@thecauldron.app" className="text-secondary underline">appeals@thecauldron.app</a> within 30 days. Appeals are reviewed by a different moderator than the one who took the original action.</p>
        </Section>

        <Section title="Contact">
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>Trust &amp; Safety:</strong> <a href="mailto:trust@thecauldron.app" className="text-secondary underline">trust@thecauldron.app</a></li>
            <li><strong>Appeals:</strong> <a href="mailto:appeals@thecauldron.app" className="text-secondary underline">appeals@thecauldron.app</a></li>
            <li><strong>Law enforcement requests:</strong> <a href="mailto:legal@thecauldron.app" className="text-secondary underline">legal@thecauldron.app</a></li>
          </ul>
        </Section>
      </div>
    </Layout>
  );
}

function Section({ title, children }) {
  return (
    <section className="parchment p-6">
      <h2 className="font-serif text-2xl mb-3 text-secondary">{title}</h2>
      <div className="text-sm leading-relaxed text-foreground/80">{children}</div>
    </section>
  );
}
